<template>
  <div id="priceCompare" class="product-compare-price">
    <div v-if="rows.length" class="product-compare-price-table">
      <div class="product-compare-table-head">
        <p class="product-compare-table-lead">
          <!-- 支給の `assets/images/img_guide001.png`。既に `src/public/image/` 直下に同名で存在する -->
          <img src="/image/img_guide001.png" alt="" class="product-compare-table-lead_icon" />
          <span class="product-compare-table-lead_mark">{{ lead }}</span>
        </p>
        <span class="product-compare-table-badge">価格比較一覧</span>
      </div>

      <table class="product-compare-table">
        <thead>
          <tr>
            <th class="product-compare-table_qty">数量</th>
            <th v-if="hasRegular" class="product-compare-table_regular">定期コース価格</th>
            <th class="product-compare-table_normal">通常購入価格</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.quantity">
            <th scope="row">
              <span class="product-compare-table_num">{{ row.quantity }}</span>{{ unit }}
            </th>
            <td v-if="hasRegular" :class="{ 'is-deal': row.regular?.deal }">
              <div v-if="row.regular" class="product-compare-table_price">
                <p>
                  <span class="product-compare-table_yen">{{ total(row.regular.unitPrice, row.quantity) }}</span>円<span
                    class="product-compare-table_tax"
                    >（税込）</span
                  >
                </p>
                <span v-if="row.regular.showUnitPrice" class="product-compare-table_unit">
                  <span>1</span>{{ unit }}あたり<span>{{ unitLabel(row.regular.unitPrice) }}</span>円（税込）
                </span>
              </div>
            </td>
            <td :class="{ 'is-deal': row.normal.deal }">
              <div class="product-compare-table_price">
                <p>
                  <span class="product-compare-table_yen">{{ total(row.normal.unitPrice, row.quantity) }}</span>円<span
                    class="product-compare-table_tax"
                    >（税込）</span
                  >
                </p>
                <span v-if="row.normal.showUnitPrice" class="product-compare-table_unit">
                  <span>1</span>{{ unit }}あたり<span>{{ unitLabel(row.normal.unitPrice) }}</span>円（税込）
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 表を持たない4商品（`NS1` `NS2` `NS3` `NST`）は注釈だけが入る -->
    <slot />
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-compare-price`（価格比較一覧）。20/38。
 * **表ありは16商品**（`210` `501` `510` `512` `513` `516` `520` `525` `530` `549` `558` `559`
 * `592` `710` `777` `810`）で、既存 `productContents/<商品コード>/priceLists.vue` を持つ16商品と完全一致。
 * **注釈のみ4商品**（`NS1` `NS2` `NS3` `NST`）は表を出さずスロットの注釈だけを描く。
 *
 * **数値は const に持つ**（決定#44）。現状は商品別 `priceLists.vue` に直書きされている。
 * 持たせるのは「1個あたりの単価」で、**総額は単価×数量で導出する**（支給16商品の全行で一致を確認）。
 *
 * **`is-deal`（強調）は導出しない。** 支給の実測では、通常セルは数量3行目以降に付くのが15商品だが
 * `549` だけ全行に付かず、定期セルは11商品が全行に付く一方 `510` `512` `513` `516` `777` は行ごとに違う。
 * 規則化すると例外6商品で崩れるため、**行データとして持つ**。
 *
 * 単価の併記（`product-compare-table_unit`）も同じ理由で行データに持たせる。支給は数量1行目の
 * 通常セルにだけ併記が無い（16/16）。
 *
 * `unit` は「箱」「袋」など商品ごとの単位。定期を持たない商品では定期列そのものを出さない。
 * → CONVERSION-GUIDE.md §3.3.1 の #9 ／ §3.3.3
 */
type Cell = {
  /** 1個あたりの税込単価 */
  unitPrice: number;
  /** `is-deal`（強調）を付けるか */
  deal?: boolean;
  /** 「1箱あたり◯円」の併記を出すか */
  showUnitPrice?: boolean;
};

type Row = {
  quantity: number;
  /** 定期コース価格。定期を持たない商品では `null` */
  regular?: Cell | null;
  normal: Cell;
};

type Props = {
  rows?: Row[];
  /** 「箱」「袋」など */
  unit?: string;
  /** 表の上の煽り文 */
  lead?: string;
};

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  unit: '個',
  lead: '定期コース、おまとめ買いが断然お得！',
});

const hasRegular = computed<boolean>(() => props.rows.some((row) => row.regular));

const total = (unitPrice: number, quantity: number): string => (unitPrice * quantity).toLocaleString();

/**
 * 「1箱あたり◯円」の併記。**割り切れない単価は切り捨てて出す。**
 *
 * 総額は単価×数量で導出する設計（上の `total`）なので、割り切れない定期セットは
 * 単価を整数で持てない。`777` の定期は `R77`「楽しまナイト3箱セット」4,480円＝1箱あたり
 * 1,493.33円で、`unitPrice: 1493` にすると総額が 4,479円 になってしまう。
 * `unitPrice: 4480 / 3` を持たせて**総額を正しく出し、併記だけ切り捨てる**。
 *
 * **切り上げ・四捨五入にしない。** 単価は目安表示なので、実際の総額より高い印象を
 * 与えない側に倒す。`C-16` で消した「1,646.667円」のような小数表記もこれで出なくなる。
 * 既存16商品の単価はすべて整数なので、この関数を通しても表示は変わらない。
 */
const unitLabel = (unitPrice: number): string => Math.floor(unitPrice).toLocaleString();
</script>
